/**
 * Tide model for teaching scenarios.
 *
 * Phase 1 is scenario-defined: a scenario carries a constant tide (set, rate,
 * height) or, optionally, a simple sinusoidal model. We do not call any tide
 * API. Real tide and weather data is phase 3.
 *
 * The teaching model below is the standard half-day cycle:
 *   - height oscillates between low and high water as a cosine over the period
 *   - tidal stream peaks halfway between low and high water, and is zero at
 *     each (slack water).
 *
 * That's enough for an exercise to ask "predict your EP at 1030 with the tide
 * given" and get a defensible answer.
 */

export interface TideCycle {
  /** Low water height in metres, chart datum. */
  lowWaterM: number;
  /** High water height in metres, chart datum. */
  highWaterM: number;
  /** Time of high water in hours since the scenario reference t=0. */
  hwHour: number;
  /** Period of the tide in hours; 12.42 for the M2 (semidiurnal) tide. */
  periodH: number;
  /** Peak stream rate in knots at mid-flood and mid-ebb. */
  peakRateKn: number;
  /** Direction the FLOOD flows TOWARDS, in degrees true. */
  floodSetDeg: number;
}

export const DEFAULT_TIDE: TideCycle = {
  lowWaterM: 0.5,
  highWaterM: 4.5,
  hwHour: 6,
  periodH: 12.42,
  peakRateKn: 1.5,
  floodSetDeg: 90,
};

export interface TideAt {
  heightM: number;
  /** Direction the stream flows TOWARDS (degrees true). */
  setDeg: number;
  rateKn: number;
}

/**
 * Sample the tide at `tHour` hours from scenario t=0.
 *
 * Height follows a cosine: peaks at hwHour, troughs half a period earlier
 * and later. Stream is zero at HW and LW (slack) and peaks halfway between,
 * flowing flood from LW->HW and ebb (180° reciprocal) from HW->LW.
 */
export function sampleTide(cycle: TideCycle, tHour: number): TideAt {
  const range = (cycle.highWaterM - cycle.lowWaterM) / 2;
  const mid = (cycle.highWaterM + cycle.lowWaterM) / 2;
  const omega = (2 * Math.PI) / cycle.periodH;
  const heightM = mid + range * Math.cos(omega * (tHour - cycle.hwHour));
  // dh/dt: flood (rising) when negative-of-derivative > 0 ... easier to think
  // in terms of phase. Stream amplitude is sin of phase relative to HW.
  // At HW (tHour = hwHour): cos = 1 so dh/dt = 0 (slack).
  // 3 hours after HW (quarter period): dh/dt is most negative (ebb), so stream
  //   flows in the EBB direction with peak magnitude.
  // We follow the convention that flood flows TOWARDS floodSetDeg and ebb
  // flows TOWARDS floodSetDeg + 180°.
  const phase = omega * (tHour - cycle.hwHour);
  const flowing = -Math.sin(phase); // negative when ebbing in our convention
  const rate = Math.abs(flowing) * cycle.peakRateKn;
  const setDeg = flowing >= 0 ? cycle.floodSetDeg : (cycle.floodSetDeg + 180) % 360;
  return { heightM, setDeg, rateKn: rate };
}
